import React, { useState } from "react";
import {
  Menu,
  Button,
  Anchor,
  Checkbox,
  Group,
  PasswordInput,
  TextInput,
  MantineProvider,
  Divider,
  Paper,
  Text,
  Box,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import fetchApi from "../Lib/api";
import Notification from "../Lib/Notification";
import { GoogleButton } from "./GoogleButton";
import { LinkedInButton } from "./LinkedInButton";
import UserButton from "../../stories/UserButton";
import {
  IconArrowRight,
  IconLogin,
  IconLock,
  IconMail,
} from "@tabler/icons-react";
import Mixpanel from "../Lib/mixpanel";

function getSubdomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  // Adjust for localhost or development environments
  if (parts.length > 2) {
    return parts[0]; // Subdomain is the first part of the hostname
  }
  return null; // No subdomain
}

export default function SignInModal({ isSignedIn, currentUser }) {
  const [resetMode, setResetMode] = useState(false);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Form validation rules
  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      remember_me: false,
    },
    validate: {
      email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
      password: (val) =>
        resetMode
          ? null
          : val.length >= 6
          ? null
          : "Password should include at least 6 characters",
    },
  });

  const handleSocialLogin = async (provider) => {
    setNotification(null);
    setLoading(true);

    Mixpanel.button(`${provider}_login`, {
      category: "authentication",
      location: "login_modal",
      provider: provider,
      login_method: "social_oauth",
      user_state: "unauthenticated",
    });

    const startTime = performance.now();

    try {
      // Store remember_me preference in session before OAuth redirect
      if (rememberMe) {
        await fetchApi('/users/auth/store_remember_me', 'POST', { remember_me: true });
      }
      
      // Redirect to backend social login endpoint
      const response = await fetchApi(`/users/auth/${provider}`, "GET");
      

      if (response.ok) {
        const data = await response.json();

        // Redirect to authorization URL or handle successful login
        if (data.redirect_url) {
          Mixpanel.custom("OAuth Redirect Started", {
            provider: provider,
            redirect_url_domain: new URL(data.redirect_url).hostname,
            auth_flow_step: "provider_redirect",
            session_context: "login_attempt",
          });
          window.location.href = data.redirect_url;
        } else {
          setNotification({
            type: "success",
            content: `${provider} login initiated`,
          });
        }
      } else {
        const errorData = await response.json();
        setNotification({
          type: "failure",
          content: errorData.error || `${provider} login failed`,
        });
      }
    } catch (error) {
      console.error(`${provider} Login Error:`, error);
      Mixpanel.error("social_auth_network_error", {
        message: error.message,
        stack: error.stack,
        component: "social_login",
        severity: "error",
        provider: provider,
        response_time: Math.round(responseTime),
        userAction: "social_login_attempt",
        user_impact: "authentication_blocked",
        error_type: "network_failure",
      });
      setNotification({
        type: "failure",
        content: `Network error with ${provider} login`,
      });
    } finally {
      setLoading(false);

      const totalTime = performance.now() - startTime;

      // Track overall social login attempt completion
      Mixpanel.performance(
        "social_login_flow",
        {
          loadTime: Math.round(totalTime),
          responseTime: Math.round(totalTime),
        },
        {
          provider: provider,
          component: "social_login",
          flow_completed: true,
          loading_duration: Math.round(totalTime),
        }
      );
    }
  };

  const handleSubmit = async (values) => {
    setNotification(null);
    setLoading(true);

    const authType = resetMode ? 'password_reset' : 'login';
  const startTime = performance.now();

    Mixpanel.form(authType, "submitted", {
      category: "authentication",
      auth_type: authType,
      form_fields: resetMode ? ["email"] : ["email", "password"],
      form_completion_time: null, // Could track time from form start if available
      subdomain_context: !!getSubdomain(),
    });

    try {
      let endpoint, payload;
      const subdomain = getSubdomain();

      if (resetMode) {
        // Password Reset
        endpoint = "/users/password";
        payload = { email: values.email };
      } else {
        // Login
        endpoint = "/users/sign_in";
        payload = {
          email: values.email,
          password: values.password,
          remember_me: values.remember_me || false,
          subdomain,
        };
      }

      const response = await fetchApi(endpoint, "POST", { user: payload });
      const responseTime = performance.now() - startTime;

      if (response.ok) {
        const data = await response.json();

        // Success handling
        if (resetMode) {
          Mixpanel.auth("password_reset_requested", "email", {
            success: true,
            response_time: Math.round(responseTime),
            email_domain: values.email.split("@")[1],
            auth_flow: "password_recovery",
          });
          // → "User Password Reset Requested via Email"

          // Track feature usage for password reset
          Mixpanel.feature("password_reset", "completed", {
            category: "authentication",
            success: true,
            method: "email_link",
            response_time: Math.round(responseTime),
          });
          setNotification({
            type: "success",
            content: "Password reset link sent to your email",
          });
          // Optional: Switch back to login mode after successful reset request
          setResetMode(false);
        } else {
          setNotification({
            type: "success",
            content: "Login successful",
          });

          Mixpanel.auth("login", "email_password", {
            success: true,
            response_time: Math.round(responseTime),
            subdomain_login: !!subdomain,
            redirect_destination: data.redirect_to || "/dashboard/#/",
            auth_flow: "direct_login",
            email_domain: values.email.split("@")[1],
          });
          // → "User Login via Email Password"

          // Track successful authentication feature usage
          Mixpanel.feature("email_authentication", "completed", {
            category: "authentication",
            success: true,
            login_method: "email_password",
            response_time: Math.round(responseTime),
          });
          // Redirect or perform login action
          window.location.href = data.redirect_to || "/dashboard/#/";
        }
      } else {
        // Error handling
        const errorData = await response.json();
        Mixpanel.auth(
          `${authType}_failed`,
          resetMode ? "email" : "email_password",
          {
            success: false,
            error_type: "authentication_rejected",
            response_time: Math.round(responseTime),
            http_status: response.status,
            retry_available: true,
          }
        );
        setNotification({
          type: "failure",
          content: errorData.error || "Authentication failed",
        });
      }
    } catch (error) {
      console.error("Authentication Error:", error);
      setNotification({
        type: "failure",
        content: "Network error. Please try again.",
      });
    } finally {
      setLoading(false);

      const totalTime = performance.now() - startTime;

      // Track overall authentication flow performance
      Mixpanel.performance(
        `${authType}_flow`,
        {
          loadTime: Math.round(totalTime),
          responseTime: Math.round(totalTime),
        },
        {
          auth_type: authType,
          component: "authentication_form",
          flow_completed: true,
          form_method: resetMode ? "password_reset" : "email_password_login",
        }
      );
    }
  };

  return (
    <MantineProvider
      theme={{
        fontSizes: {
          xs: "12px",
          sm: "14px",
          md: "16px",
          lg: "18px",
          xl: "20px",
        },
        colors: {
          brand: [
            "#e3f2fd",
            "#bbdefb",
            "#90caf9",
            "#64b5f6",
            "#42a5f5",
            "#2196f3",
            "#1e88e5",
            "#1976d2",
            "#1565c0",
            "#0d47a1",
          ],
        },
        primaryColor: "brand",
        components: {
          TextInput: {
            defaultProps: {
              size: "md",
              radius: "md",
            },
            styles: (theme) => ({
              input: {
                height: "44px",
                fontSize: "14px",
                border: `1px solid ${theme.colors.gray[3]}`,
                "&:focus": {
                  borderColor: theme.colors.brand[5],
                  boxShadow: `0 0 0 2px ${theme.colors.brand[1]}`,
                },
              },
              label: {
                fontSize: "14px",
                fontWeight: 500,
                color: theme.colors.gray[7],
                marginBottom: "6px",
              },
            }),
          },
          PasswordInput: {
            defaultProps: {
              size: "md",
              radius: "md",
            },
            styles: (theme) => ({
              input: {
                height: "44px",
                fontSize: "14px",
                border: `1px solid ${theme.colors.gray[3]}`,
                "&:focus": {
                  borderColor: theme.colors.brand[5],
                  boxShadow: `0 0 0 2px ${theme.colors.brand[1]}`,
                },
              },
              label: {
                fontSize: "14px",
                fontWeight: 500,
                color: theme.colors.gray[7],
                marginBottom: "6px",
              },
            }),
          },
          Button: {
            styles: (theme) => ({
              root: {
                height: "44px",
                fontSize: "14px",
                fontWeight: 600,
                borderRadius: theme.radius.md,
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: theme.shadows.md,
                },
              },
            }),
          },
        },
      }}
    >
      {isSignedIn ? (
        <UserButton
          userName={currentUser?.full_name}
          userEmail={currentUser?.email}
          userImage={""}
          dashboard={true}
        />
      ) : (
        <Menu
          shadow="xl"
          position="bottom-end"
          offset={8}
          transitionProps={{ transition: "scale-y", duration: 300 }}
          styles={(theme) => ({
            dropdown: {
              border: `1px solid ${theme.colors.gray[2]}`,
              borderRadius: theme.radius.lg,
              overflow: "hidden",
            },
          })}
        >
          <Menu.Target>
            <button className="group relative bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 hover:shadow-xl flex items-center gap-2">
              <IconLogin size={18} />
              Sign In
              <IconArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </Menu.Target>

          <Menu.Dropdown p={0} radius="lg">
            <Paper p={32} radius="lg" style={{ width: "380px" }}>
              {/* Header */}
              <Box mb={24}>
                <Text size="xl" fw={700} ta="center" c="dark.8" mb={4}>
                  {resetMode ? "Reset Password" : "Welcome Back"}
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  {resetMode
                    ? "Enter your email to receive a reset link"
                    : "Sign in to your account to continue"}
                </Text>
              </Box>

              {notification && (
                <Box mb={20}>
                  <Notification {...notification} />
                </Box>
              )}

              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap={16}>
                  <TextInput
                    label="Email Address"
                    placeholder="Enter your email"
                    required
                    icon={<IconMail size={18} />}
                    {...form.getInputProps("email")}
                  />

                  {!resetMode && (
                    <PasswordInput
                      label="Password"
                      placeholder="Enter your password"
                      required
                      icon={<IconLock size={18} />}
                      {...form.getInputProps("password")}
                    />
                  )}

                  <Group justify="space-between" mt={8}>
                    {!resetMode && (
                      <Checkbox
                        label="Remember me"
                        size="sm"
                        checked={rememberMe}
                        onChange={(event) => {
                          setRememberMe(event.currentTarget.checked);
                          form.setFieldValue("remember_me", event.currentTarget.checked);
                        }}
                        styles={(theme) => ({
                          label: {
                            fontSize: "13px",
                            color: theme.colors.gray[6],
                          },
                        })}
                      />
                    )}
                    <Anchor
                      component="button"
                      size="sm"
                      type="button"
                      fw={500}
                      c="brand.6"
                      onClick={() => {
                        setResetMode(!resetMode);
                        form.reset();
                      }}
                      style={{
                        textDecoration: "none",
                        marginLeft: resetMode ? "auto" : 0,
                      }}
                      className="hover:underline transition-all duration-200"
                    >
                      {resetMode ? "← Back to Login" : "Forgot password?"}
                    </Anchor>
                  </Group>

                  <Button
                    fullWidth
                    mt={20}
                    type="submit"
                    loading={loading}
                    size="md"
                    gradient={{ from: "brand.6", to: "brand.7", deg: 135 }}
                    styles={(theme) => ({
                      root: {
                        height: "48px",
                        fontSize: "15px",
                        fontWeight: 600,
                        boxShadow: theme.shadows.sm,
                      },
                    })}
                  >
                    {resetMode ? "Send Reset Link" : "Sign In"}
                  </Button>

                  {!resetMode && (
                    <>
                      <Divider
                        label="Or continue with"
                        labelPosition="center"
                        my={20}
                        styles={(theme) => ({
                          label: {
                            fontSize: "13px",
                            color: theme.colors.gray[5],
                            fontWeight: 500,
                          },
                        })}
                      />

                      <Group grow gap={12}>
                        <GoogleButton
                          radius="md"
                          size="md"
                          onClick={() => handleSocialLogin("google")}
                          styles={{
                            root: {
                              height: "44px",
                              fontSize: "14px",
                              border: "1px solid #e5e7eb",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                              },
                            },
                          }}
                        >
                          Google
                        </GoogleButton>
                        <LinkedInButton
                          radius="md"
                          size="md"
                          onClick={() => handleSocialLogin("linkedin")}
                          styles={{
                            root: {
                              height: "44px",
                              fontSize: "14px",
                              border: "1px solid #e5e7eb",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                              },
                            },
                          }}
                        >
                          LinkedIn
                        </LinkedInButton>
                      </Group>
                    </>
                  )}
                </Stack>
              </form>
            </Paper>
          </Menu.Dropdown>
        </Menu>
      )}
    </MantineProvider>
  );
}
